import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashOmit from 'lodash/fp/omit';

import withoutId from 'common/util/withoutId';
import leaseDraftingFields from 'common/leaseDrafting/leaseDraftingFields';

import {
  SR_API_ACTION_LEASE_DRAFT_UPSERT_CONFIGURATION,
} from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';
import LeaseActions from 'client/actions/Lease';
import useIsMounted from 'client/hooks/useIsMounted';

import {
  createFormValidationFn,
  createInitialValues,
} from 'client/helpers/formikHelpers';

import SrCard from 'client/components/SrCard/SrCard';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrTable from 'client/components/SrTable';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';

const leaseDraftConfigurationFormFields = [
  {
    id: 'id',
    title: '',
    type: 'hidden',
  },
  {
    id: 'partnerRecipients',
    title: 'Custom partner recipients',
    subtitle: (
      'Please use JSON for each lease recipient: ' +
      '{ "name": "PropManagement", "email": "manager@propmanagement.com" }'
    ),
    type: 'text_list',
    properties: {
      noMargin: true,
    },
  },
  {
    id: 'partnerRecipientLast',
    title: 'Partner recipient last on list',
    type: 'checkbox',
  },
  {
    id: 'createdDate',
    title: '',
    type: 'hidden',
  },
  {
    id: 'fieldOverrides',
    title: '',
    type: 'hidden',
  },
  {
    id: 'partnerId',
    title: '',
    type: 'hidden',
  },
];

const leaseDraftFieldOverrideFormFields = [
  {
    id: 'fieldId',
    title: 'Override field',
    type: 'select',
    properties: {
      choices: leaseDraftingFields
        .filter(({ apiOnly }) => !apiOnly)
        .map(({ id, title }) => ({
          label: title,
          value: id,
        })),
    },
    validations: ['required'],
  },
  {
    id: 'value',
    title: 'Override value',
    type: 'short_text',
    properties: {
      placeholder: 'Write here...',
    },
    validations: ['required'],
  },
];

const getInitialMenuFormFields = ({
  draftConfigDropdownOptions,
  createDraftConfigDropdownOptions,
  onSelectDraftConfig,
}) => [
  {
    id: 'updateDraftConfigPartnerId',
    title: 'Select a lease draft to configure',
    type: 'select',
    properties: {
      placeholder: 'select...',
      enableResetOption: true,
      choices: draftConfigDropdownOptions,
      onChange: onSelectDraftConfig,
      noMargin: true,
    },
  },
  {
    id: 'createDraftConfigPartnerId',
    title: 'Or, select a partner to create a new lease draft configuration',
    type: 'select',
    properties: {
      placeholder: 'select...',
      enableResetOption: true,
      choices: createDraftConfigDropdownOptions,
    },
    validations: ['required'],
  },
];

const SrLeaseDraftFieldOverrideForm = ({ onSubmit }) => (
  <Formik
    enableReinitialize
    initialValues={createInitialValues({ formFields: leaseDraftFieldOverrideFormFields })}
    validate={createFormValidationFn(leaseDraftFieldOverrideFormFields)}
    render={(formikBag) => {
      return (
        <div>
          {leaseDraftFieldOverrideFormFields.map((formField) => (
            <SrFormikField
              key={formField.id}
              formField={formField}
              formikBag={formikBag}
              theme={SR_FIELD_THEME_ADOBE}
            />
          ))}
          <SrBox padding='23px 0'>
            <SrButton
              type='button'
              theme={SR_BUTTON_THEME_ADOBE_BLUE}
              disabled={!formikBag.isValid}
              onClick={() => {
                onSubmit(formikBag.values);
                formikBag.resetForm();
              }}
            >
              Add
            </SrButton>
          </SrBox>
        </div>
      );
    }}
  />
);

SrLeaseDraftFieldOverrideForm.displayName = 'SrLeaseDraftFieldOverrideForm';

const SrLeaseDraftConfigurationsAdmin = ({ leaseDraftConfigurationList, partnerList, dispatch }) => {
  const [editingDraftConfig, setEditingDraftConfig] = useState(null);
  const checkIsMounted = useIsMounted();

  const onSelectDraftConfig = useCallback((event) => {
    let { value } = event.target;

    if (!value) {
      return;
    }

    if (typeof value === 'string' && !isNaN(value)) {
      value = parseInt(value, 10);
    }

    const selectedLeaseDraftConfig = leaseDraftConfigurationList.find((draftConfig) => {
      return (
        draftConfig.partnerId === value
        || (!draftConfig.partnerId && value === 'standard')
      );
    });
    setEditingDraftConfig(selectedLeaseDraftConfig);
  }, [leaseDraftConfigurationList]);

  const onCreateDraftConfig = useCallback(({ createDraftConfigPartnerId }) => {
    createDraftConfigPartnerId = parseInt(createDraftConfigPartnerId, 10);
    const standardLeaseDraftConfig = leaseDraftConfigurationList.find((draftConfig) => {
      return !draftConfig.partnerId;
    });
    const partnerLeaseDraftConfig = {
      ...withoutId(standardLeaseDraftConfig),
      partnerId: createDraftConfigPartnerId,
    };
    setEditingDraftConfig(partnerLeaseDraftConfig);
  }, [leaseDraftConfigurationList]);

  const onSubmit = useCallback((updatedDraftConfig) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_LEASE_DRAFT_UPSERT_CONFIGURATION,
      payload: {
        leaseDraftConfiguration: updatedDraftConfig,
      },
    })
      .then(() => {
        return dispatch(LeaseActions.getPartnerDraftConfigurationList, {
          refresh: true,
        });
      })
      .then(() => {
        if (checkIsMounted()) {
          setEditingDraftConfig(null);
        }
      });
  }, [dispatch, checkIsMounted]);

  const onCancelClick = useCallback((formChanged) => {
    if (formChanged && !global.confirm('Are you sure you want to cancel? The changes will be lost.')) {
      return;
    }
    setEditingDraftConfig(null);
  }, []);

  const partnerIdToDisplayNameMapping = partnerList.reduce((memo, { id, displayName }) => {
    memo[id] = displayName;
    return memo;
  }, {});

  const partnersWithConfigsDropdownOptions = leaseDraftConfigurationList
    .filter(({ partnerId }) => partnerId)
    .map((leaseDraftConfiguration) => ({
      label: partnerIdToDisplayNameMapping[leaseDraftConfiguration.partnerId],
      value: leaseDraftConfiguration.partnerId,
    }));

  const draftConfigDropdownOptions = [
    {
      label: 'Standard',
      value: 'standard',
    },
    ...partnersWithConfigsDropdownOptions,
  ];

  const partnerIdsWithConfigs = partnersWithConfigsDropdownOptions.map(({ value }) => value);

  const createDraftConfigDropdownOptions = partnerList
    .filter(({ id }) => !partnerIdsWithConfigs.includes(id))
    .map((partner) => ({
      label: partner.displayName,
      value: partner.id,
    }));

  return (
    <SrCard>
      {!editingDraftConfig && (
        <Formik
          onSubmit={onCreateDraftConfig}
          render={(formikBag) => {
            const initialMenuFormFields = getInitialMenuFormFields({
              draftConfigDropdownOptions,
              createDraftConfigDropdownOptions,
              onSelectDraftConfig,
            });
            return (
              <Form>
                {initialMenuFormFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
                <SrBox paddingTop='23px'>
                  <SrButton
                    type='submit'
                    theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                    disabled={!formikBag.isValid}
                  >
                    Create draft configuration
                  </SrButton>
                </SrBox>
              </Form>
            );
          }}
        />
      )}

      {editingDraftConfig &&
        <Formik
          enableReinitialize
          initialValues={editingDraftConfig}
          validate={createFormValidationFn(leaseDraftConfigurationFormFields)}
          onSubmit={onSubmit}
          render={(formikBag) => {
            const onSubmitFieldOverride = ({ fieldId, value }) => {
              formikBag.setFieldValue('fieldOverrides', {
                ...formikBag.values.fieldOverrides,
                [fieldId]: value,
              });
            };
            const fieldOverridesList = Object.keys(formikBag.values.fieldOverrides || {})
              .map((fieldId) => {
                return {
                  title: leaseDraftingFields.find(({ id }) => id === fieldId)?.title,
                  fieldId: fieldId,
                  value: formikBag.values.fieldOverrides[fieldId],
                };
              });

            const editingPartner = partnerList.find(({ id }) => id === editingDraftConfig.partnerId);

            const leaseDraftConfigEditTitle = editingDraftConfig.partnerId
              ? `Editing the config for ${editingPartner?.displayName}`
              : 'Editing the standard config';

            return (
              <Form>
                <SrText
                  font='greycliff'
                  size='28'
                  weight='700'
                  color='blueGreycliff'
                  display='block'
                  paddingBottom='23px'
                >
                  {leaseDraftConfigEditTitle}
                </SrText>
                {leaseDraftConfigurationFormFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
                <SrLeaseDraftFieldOverrideForm onSubmit={onSubmitFieldOverride} />
                <SrBox>
                  <SrTable
                    minWidth='auto'
                    headers={[
                      { content: 'title' },
                      { content: 'field id' },
                      { content: 'value' },
                      { content: 'actions' },
                    ]}
                    rows={fieldOverridesList.map(({ title, fieldId, value }) => ({
                      cells: [
                        { content: title },
                        { content: fieldId },
                        { content: value },
                        {
                          content: (
                            <span
                              onClick={() => {
                                if (!global.confirm(
                                  `Are you sure you want to remove the field override "${fieldId}"`
                                )) {
                                  return;
                                }
                                formikBag.setFieldValue(
                                  'fieldOverrides',
                                  lodashOmit(fieldId, formikBag.values.fieldOverrides),
                                );
                              }}
                            >
                              Remove
                            </span>
                          ),
                        },
                      ],
                    }))}
                  />
                </SrBox>
                <SrBox padding='23px 0'>
                  <SrButton
                    type='submit'
                    theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                    disabled={!formikBag.isValid}
                  >
                    Save
                  </SrButton>
                </SrBox>
                <SrButton
                  type='button'
                  theme={SR_BUTTON_THEME_ADOBE_GREY}
                  onClick={() => onCancelClick(formikBag.dirty)}
                >
                  Cancel
                </SrButton>
              </Form>
            );
          }}
        />
      }
    </SrCard>

  );
};

SrLeaseDraftConfigurationsAdmin.propTypes = {
  leaseDraftConfigurationList: PropTypes.array,
  partnerList: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrLeaseDraftConfigurationsAdmin.displayName = 'SrLeaseDraftConfigurationsAdmin';

export default SrLeaseDraftConfigurationsAdmin;
